import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import SearchBar from "@/components/base/SearchBar";
import SiteCard from "@/components/feature/SiteCard";
import { useSEO } from "@/hooks/useSEO";
import { getCategories, searchSites } from "@/lib/navData";

const PAGE_SIZE = 12;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("query") ?? "";
  const categoryFilter = searchParams.get("category") ?? "全部";

  const [page, setPage] = useState(1);

  useSEO(
    query ? `“${query}”的搜索结果 - 51网址导航` : "搜索网址 - 51网址导航",
    `在51网址导航中搜索“${query}”，快速找到相关网站入口，支持按分类筛选结果。`,
    `/search?query=${encodeURIComponent(query)}`,
  );

  const categories = getCategories();

  const results = useMemo(
    () => searchSites(query, categoryFilter === "全部" ? undefined : categoryFilter),
    [query, categoryFilter],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const changeCategory = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部") {
      next.delete("category");
    } else {
      next.set("category", value);
    }
    setSearchParams(next);
    setPage(1);
  };

  return (
    <div className="max-w-page mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <div className="mt-5 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200">
        <h1 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">
          {query ? (
            <>
              搜索结果：<span className="text-primary-600">“{query}”</span>
            </>
          ) : (
            "搜索网址"
          )}
        </h1>
        <p className="text-sm text-foreground-500 mt-1.5">
          {query
            ? `找到 ${results.length} 条相关网址，可修改关键词或按分类筛选。`
            : "输入网站名称或关键词，快速找到你需要的网站入口。"}
        </p>
        <div className="mt-4 max-w-xl">
          <SearchBar key={query} defaultValue={query} size="lg" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6">
        <button
          type="button"
          onClick={() => changeCategory("全部")}
          className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
            categoryFilter === "全部"
              ? "bg-primary-500 text-background-50"
              : "bg-background-100 text-foreground-600 hover:bg-background-200"
          }`}
        >
          全部分类
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => changeCategory(cat.id)}
            className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              categoryFilter === cat.id
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {pageItems.length === 0 ? (
          <div className="flex flex-col gap-6">
            <EmptyState
              icon="ri-emotion-sad-line"
              title="未找到相关网址"
              description="换个关键词试试，或从下面的热门分类继续浏览。"
              actionLabel="返回首页"
              actionTo="/"
            />
            <div>
              <h2 className="font-heading font-semibold text-base text-foreground-950 mb-3">热门分类推荐</h2>
              <div className="flex flex-wrap gap-2">
                {categories.slice(0, 8).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${encodeURIComponent(cat.id)}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={cat.icon} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              {pageItems.map((site) => (
                <SiteCard key={site.id} site={site} />
              ))}
            </div>
            <Pagination currentPage={safePage} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}